import {
  ExternalServiceType,
  IAutomationActionTemplateSyncToDataDestination,
  IExperienceTemplateResource,
  IExperienceTemplateResourceSalesforce,
  InternalStorageInstructionType,
  SyncToDataDestinationFieldMapping,
  SyncToDataDestinationTarget,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UKG_WRITE_FIELDS,
  ukgFieldRequiresReasonCode
} from '@dadohr/shared/domainTypes/integrations/ukg';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import shortid from 'shortid';
import {
  AutocompleteSingle,
  AutocompleteSingleSimpleKeyed,
  AutocompleteSingleWithCreateOption
} from '../../../../../../../components/Autocomplete';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { TextFieldLoader } from '../../../../../../../components/TextFieldLoader';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { useIntegrationsContext } from '../../../../../../../services/integrations/context';
import { toSearchTermRegexp, useUserFields } from '../../../../../../../services/user';
import { useSalesforceObjects } from '../../../../../../Org/pages/Integrations/integrations/salesforce/service';
import {
  useWorkdayCustomObjects,
  useWorkdayObjectFields,
  WorkdayCustomObjectDef
} from '../../../../../../Org/pages/Integrations/integrations/workday/service';
import { MappingRows } from '../MappingRows';
import { EditSalesforceResourceDialog } from '../../../TaskBuilder/components/BlockSettings/SubTask/EditSalesforceResourceDialog';
import {
  SpreadsheetResourcePicker,
  UkgReasonCodeField
} from '../../../TaskBuilder/components/BlockSettings/SubTask/dataDestinations';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { AutomationBuilderActionValidationStateItem } from '../../validation';
import {
  createEmptyDestination,
  getSyncDestinationDefinition,
  readSyncConfig,
  SYNC_DESTINATIONS,
  SyncDestinationType
} from './helpers';

/**
 * @guide Create a new automation action - 4.1 Add the Settings component
 *
 * Authoring UI for the "Sync to Data Destination" action. The saved shape
 * matches the backend contract exactly:
 *   { destination: SyncToDataDestinationTarget; mappings: {sourceField,target}[] }
 * — one destination for the whole action, plus N Dado-field → target rows.
 */

type DadoFieldOption = { key: string; label: string };

const useDadoFieldOptions = (
  customFields: CustomUserField[]
): DadoFieldOption[] => {
  const userFields = useUserFields(customFields);
  return useMemo(
    () => userFields.map((f) => ({ key: f.key, label: f.label })),
    [userFields]
  );
};

const targetLabel = (t: (k: string) => string, type: SyncDestinationType) => {
  switch (type) {
    case ExternalServiceType.GOOGLE_SHEET:
    case ExternalServiceType.MS_EXCEL:
      return t('Written to the column');
    case ExternalServiceType.SALESFORCE:
      return t('Salesforce field API name');
    case ExternalServiceType.UKG:
      return t('UKG Pro field');
    case InternalStorageInstructionType.USER_PROFILE:
      return t('Dado field to write to');
    default:
      return t('Written to the field');
  }
};

/** The "Data destination" dropdown. */
const DestinationSelect = ({
  value,
  onChange
}: {
  value: SyncDestinationType | '';
  onChange: (nextType: SyncDestinationType) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { active: activeIntegrations } = useIntegrationsContext();
  const id = 'sync-to-data-destination-type';
  const options = useMemo(
    () =>
      SYNC_DESTINATIONS.filter(
        (d) =>
          d.integration === null ||
          activeIntegrations.has(d.integration) ||
          d.type === value
      ),
    [activeIntegrations, value]
  );
  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel id={id}>{t('Data destination')}</InputLabel>
      <Select
        labelId={id}
        label={t('Data destination')}
        value={value}
        onChange={(ev) => onChange(ev.target.value as SyncDestinationType)}
      >
        {options.map((d) => (
          <MenuItem key={d.type} value={d.type}>
            {t(d.label)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Google Sheet / Excel document picker → sets `destination.resourceId`.
 *
 * Thin adapter over the shared {@link SpreadsheetResourcePicker} so this action
 * uses the exact same create + edit document dropdown as the subtask data
 * destinations (AnswerQuestion, Background Check, …).
 */
const SheetExcelResourcePicker = ({
  destination,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  customFields
}: {
  destination: Extract<
    SyncToDataDestinationTarget,
    { type: ExternalServiceType.GOOGLE_SHEET | ExternalServiceType.MS_EXCEL }
  >;
  onChange: (next: SyncToDataDestinationTarget) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (next: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FormRow>
      <SpreadsheetResourcePicker
        resourceType={
          destination.type === ExternalServiceType.GOOGLE_SHEET
            ? TemplateResourceType.GOOGLE_SHEET
            : TemplateResourceType.MS_EXCEL
        }
        resourceId={destination.resourceId}
        onSelectResourceId={(resourceId) =>
          onChange({ ...destination, resourceId })
        }
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        label={t('Document')}
        autoCreateDefault
        hideEnrollmentToggle
        allowEmptyMappings
        seedDefaultMappings={false}
      />
    </FormRow>
  );
};

/** Salesforce resource + object pickers → `resourceId` + `objectName`. */
const SalesforceResourcePicker = ({
  destination,
  onChange,
  resources,
  onChangeResources,
  expTpl
}: {
  destination: Extract<
    SyncToDataDestinationTarget,
    { type: ExternalServiceType.SALESFORCE }
  >;
  onChange: (next: SyncToDataDestinationTarget) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (next: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const [objectsResp] = useSalesforceObjects(expTpl.orgId);

  const sfResources = resources.filter(
    (r): r is IExperienceTemplateResourceSalesforce =>
      r.type === TemplateResourceType.SALESFORCE
  );
  const selected =
    sfResources.find((r) => r.resourceId === destination.resourceId) || null;

  const upsertResource = (next: IExperienceTemplateResourceSalesforce) => {
    const exists = sfResources.some((r) => r.resourceId === next.resourceId);
    onChangeResources(
      exists
        ? resources.map((r) =>
            r.type === TemplateResourceType.SALESFORCE &&
            r.resourceId === next.resourceId
              ? next
              : r
          )
        : [...resources, next]
    );
    onChange({ ...destination, resourceId: next.resourceId });
  };

  const objectOptions = useMemo(
    () =>
      (objectsResp?.objects || [])
        .filter((o) => o.createable || o.updateable)
        .map((o) => ({ key: o.name, label: `${o.label} (${o.name})` })),
    [objectsResp]
  );

  return (
    <>
      <FormRow>
        <AutocompleteSingleWithCreateOption
          fullWidth
          label={t('Salesforce destination')}
          value={selected as any}
          onChange={(r) => r && onChange({ ...destination, resourceId: r.resourceId })}
          options={sfResources}
          getOptionLabel={(o) =>
            o.objectName || t('Untitled Salesforce destination')
          }
          renderOption={(o) => (
            <span>{o.objectName || t('Untitled Salesforce destination')}</span>
          )}
          alwaysShowCreateOption
          renderCreateOption={(inputValue) =>
            inputValue
              ? `${t('Create')} "${inputValue}"`
              : t('Add a Salesforce destination')
          }
          renderCreateDialog={({ inputValue, close }) => {
            const fresh: IExperienceTemplateResourceSalesforce = {
              type: TemplateResourceType.SALESFORCE,
              resourceId: shortid(),
              status: 'PENDING',
              objectName: inputValue || 'Contact',
              externalIdField: '',
              fieldMap: {}
            };
            return (
              <EditSalesforceResourceDialog
                open={true}
                orgId={expTpl.orgId}
                value={fresh}
                onChange={(next) => upsertResource(next)}
                onClose={close}
              />
            );
          }}
        />
      </FormRow>
      {!!selected && (
        <FormRow>
          <AutocompleteSingleSimpleKeyed
            label={t('Salesforce object')}
            fullWidth
            value={destination.objectName || ''}
            options={objectOptions}
            toSelectKey={(o) => o.key}
            toLabel={(o) => o.label}
            onChange={(objectName) => onChange({ ...destination, objectName })}
          />
        </FormRow>
      )}
    </>
  );
};

/** The repeatable "Dado field → target" rows. */
const MappingsEditor = ({
  mappings,
  onChange,
  sourceOptions,
  destinationType
}: {
  mappings: SyncToDataDestinationFieldMapping[];
  onChange: (next: SyncToDataDestinationFieldMapping[]) => void;
  sourceOptions: DadoFieldOption[];
  destinationType: SyncDestinationType;
}) => {
  const { t } = useUnsafeTranslation();
  const label = targetLabel(t, destinationType);

  return (
    <MappingRows
      rows={mappings}
      onChange={onChange}
      createEmptyRow={() => ({ sourceField: '', target: '' })}
      addLabel={t('Add Dado field')}
      renderSource={(m, update) => (
        <AutocompleteSingleSimpleKeyed
          label={t('Dado field')}
          fullWidth
          value={m.sourceField}
          options={sourceOptions}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          onChange={(sourceField) => update({ ...m, sourceField })}
        />
      )}
      renderTarget={(m, update) =>
        destinationType === InternalStorageInstructionType.USER_PROFILE ? (
          // For the Dado-field destination the target is a Dado user field key
          // (it composes into `SubTaskDataDestinationUserProfile.field`), so it
          // must be picked from the known fields — a free-text value can't
          // produce a valid key. Mirrors the subtask `UserProfileSettings`.
          <AutocompleteSingleSimpleKeyed
            label={label}
            fullWidth
            value={m.target}
            options={sourceOptions}
            toSelectKey={(o) => o.key}
            toLabel={(o) => o.label}
            onChange={(target) => update({ ...m, target })}
          />
        ) : destinationType === ExternalServiceType.UKG ? (
          // UKG targets are the curated `UKG_WRITE_FIELDS` catalog keys
          // (`"<SoapService>.<EntityField>"`) — a free-text value can't produce a
          // valid write path, so pick from the catalog (mirrors the subtask
          // `UkgSettings`).
          <AutocompleteSingleSimpleKeyed
            label={label}
            fullWidth
            value={m.target}
            options={UKG_WRITE_FIELDS}
            toSelectKey={(o) => o.key}
            toLabel={(o) => o.label}
            onChange={(target) => update({ ...m, target })}
          />
        ) : (
          <TextField
            variant="outlined"
            fullWidth
            label={label}
            value={m.target}
            onChange={(ev) => update({ ...m, target: ev.target.value })}
          />
        )
      }
    />
  );
};

/**
 * The Workday target cell for one mapping row: a live two-level
 * object → field picker (identical to the subtask `WorkdaySettings`) that
 * emits the `"<customObjectAlias>.<fieldAlias>"` Web Service Alias pair the
 * Workday custom-object writer expects. The custom-object list is fetched once
 * by {@link WorkdayMappingsEditor} and passed in; each row fetches only its
 * selected object's fields.
 */
const WorkdayTargetPicker = ({
  orgId,
  objects,
  value,
  onChange
}: {
  orgId: string;
  objects: WorkdayCustomObjectDef[];
  value: string;
  onChange: (next: string) => void;
}) => {
  const { t } = useUnsafeTranslation();

  // Stored `value` is "{objectAlias}.{fieldAlias}" — split on the FIRST dot.
  const dot = value.indexOf('.');
  const objAlias = dot >= 0 ? value.slice(0, dot) : value;
  const fieldAlias = dot >= 0 ? value.slice(dot + 1) : '';

  const selectedObject = objects.find((o) => o.alias === objAlias) || null;
  const [fieldsResp, fieldsLoading] = useWorkdayObjectFields(
    orgId,
    selectedObject?.id ?? ''
  );
  const fields = fieldsResp?.fields ?? [];
  const selectedField = fields.find((f) => f.alias === fieldAlias) || null;

  return (
    <FlexContainerVertical spacing={1} fullWidth alignItems="stretch">
      <AutocompleteSingle
        label={t('Custom object')}
        value={selectedObject}
        options={objects}
        // Selecting an object resets the field (store "alias." until a field is picked).
        onChange={(o) => o && onChange(`${o.alias}.`)}
        getOptionLabel={(o) => o.name}
        renderOption={(o) => o.name}
        getOptionSelected={(a, b) => a.id === b.id}
        filterOptions={(os, s) => {
          const re = toSearchTermRegexp(s.inputValue);
          return os.filter((o) => o.name.match(re) || o.alias.match(re));
        }}
        fullWidth
      />
      {selectedObject &&
        (fieldsLoading && !fieldsResp ? (
          <TextFieldLoader
            label={t('Field')}
            placeholder={t('Loading fields...')}
            fullWidth
          />
        ) : (
          <AutocompleteSingle
            label={t('Field')}
            value={selectedField}
            options={fields}
            onChange={(f) =>
              f && onChange(`${selectedObject.alias}.${f.alias}`)
            }
            getOptionLabel={(o) => o.name}
            renderOption={(o) => `${o.name}  ·  ${o.fieldType}`}
            getOptionSelected={(a, b) => a.id === b.id}
            filterOptions={(os, s) => {
              const re = toSearchTermRegexp(s.inputValue);
              return os.filter((o) => o.name.match(re) || o.alias.match(re));
            }}
            fullWidth
          />
        ))}
      {selectedField &&
        (selectedField.kind === 'reference' ||
          selectedField.kind === 'referenceList') && (
        <InfoBox color="blue">
          {t(
            '“{{ name }}” is a {{ kind }} field — Workday expects a valid instance ID here, not a display name. Map the Dado field to the Workday ID value.',
            {
              name: selectedField.name,
              kind:
                selectedField.kind === 'referenceList'
                  ? t('multi-reference')
                  : t('reference')
            }
          )}
        </InfoBox>
      )}
    </FlexContainerVertical>
  );
};

/**
 * Workday variant of {@link MappingsEditor}: the target of each Dado-field row
 * is a live Workday object → field picker instead of free text. Rendered only
 * when the destination is Workday, so the `workday_api_*` callables never fire
 * for other destinations.
 */
const WorkdayMappingsEditor = ({
  mappings,
  onChange,
  sourceOptions,
  orgId
}: {
  mappings: SyncToDataDestinationFieldMapping[];
  onChange: (next: SyncToDataDestinationFieldMapping[]) => void;
  sourceOptions: DadoFieldOption[];
  orgId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [objectsResp, objectsLoading] = useWorkdayCustomObjects(orgId);
  const objects = objectsResp?.objects ?? [];

  if (objectsLoading && !objectsResp) {
    return (
      <TextFieldLoader
        label={t('Workday custom object')}
        placeholder={t('Loading Workday objects...')}
        fullWidth
      />
    );
  }

  if (objects.length === 0) {
    return (
      <InfoBox color="blue">
        {t(
          'No REST-writable Workday custom objects were found for this organization. Check the Workday API connection and that the integration user has Custom Object access.'
        )}
      </InfoBox>
    );
  }

  return (
    <MappingRows
      rows={mappings}
      onChange={onChange}
      createEmptyRow={() => ({ sourceField: '', target: '' })}
      addLabel={t('Add Dado field')}
      renderSource={(m, update) => (
        <AutocompleteSingleSimpleKeyed
          label={t('Dado field')}
          fullWidth
          value={m.sourceField}
          options={sourceOptions}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          onChange={(sourceField) => update({ ...m, sourceField })}
        />
      )}
      renderTarget={(m, update) => (
        <WorkdayTargetPicker
          orgId={orgId}
          objects={objects}
          value={m.target}
          onChange={(target) => update({ ...m, target })}
        />
      )}
    />
  );
};

export const ActionSettingsSyncToDataDestination = ({
  value,
  onChange,
  expTpl,
  validation,
  resources,
  onChangeResources,
  customFields
}: {
  value: IAutomationActionTemplateSyncToDataDestination;
  onChange: (nextValue: IAutomationActionTemplateSyncToDataDestination) => void;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  customFields: CustomUserField[];
}) => {
  const sourceOptions = useDadoFieldOptions(customFields);

  // Null-safe read of the action's { destination, mappings }.
  const cfg = readSyncConfig(value);
  const destination =
    cfg.destination || createEmptyDestination(ExternalServiceType.GOOGLE_SHEET);
  const mappings = cfg.mappings;

  const commit = (next: {
    destination: SyncToDataDestinationTarget;
    mappings: SyncToDataDestinationFieldMapping[];
  }) =>
    onChange({
      id: value.id,
      type: value.type,
      hooks: value.hooks,
      destination: next.destination,
      mappings: next.mappings
    });

  const setDestinationType = (type: SyncDestinationType) => {
    // Switching destination clears the mappings along with the resource/object-specific fields.
    if (type === destination.type) return;
    commit({ destination: createEmptyDestination(type), mappings: [] });
  };

  const storagesError = validation?.fields?.storages || null;

  return (
    <FlexContainerVertical spacing={2} fullWidth alignItems="stretch">

      <FormRow>
        <DestinationSelect value={destination.type} onChange={setDestinationType} />
      </FormRow>

      {(destination.type === ExternalServiceType.GOOGLE_SHEET ||
        destination.type === ExternalServiceType.MS_EXCEL) && (
        <SheetExcelResourcePicker
          destination={destination}
          onChange={(nextDestination) =>
            commit({ destination: nextDestination, mappings })
          }
          resources={resources}
          onChangeResources={onChangeResources}
          expTpl={expTpl}
          customFields={customFields}
        />
      )}

      {destination.type === ExternalServiceType.SALESFORCE && (
        <SalesforceResourcePicker
          destination={destination}
          onChange={(nextDestination) =>
            commit({ destination: nextDestination, mappings })
          }
          resources={resources}
          onChangeResources={onChangeResources}
          expTpl={expTpl}
        />
      )}

      {destination.type === ExternalServiceType.WORKDAY ? (
        <WorkdayMappingsEditor
          mappings={mappings}
          onChange={(nextMappings) => commit({ destination, mappings: nextMappings })}
          sourceOptions={sourceOptions}
          orgId={expTpl.orgId}
        />
      ) : (
        <MappingsEditor
          mappings={mappings}
          onChange={(nextMappings) => commit({ destination, mappings: nextMappings })}
          sourceOptions={sourceOptions}
          destinationType={destination.type}
        />
      )}

      {destination.type === ExternalServiceType.UKG &&
        mappings.some((m) => ukgFieldRequiresReasonCode(m.target)) && (
          <FormRow>
            <UkgReasonCodeField
              value={destination.reasonCode ?? ''}
              onChange={(reasonCode) =>
                commit({ destination: { ...destination, reasonCode }, mappings })
              }
            />
          </FormRow>
        )}

      {storagesError && <InfoBox color="red">{storagesError}</InfoBox>}
    </FlexContainerVertical>
  );
};

/**
 * @guide Create a new automation action - 4.1 Add the Settings component
 * (read-only variant).
 */
export const ActionSettingsSyncToDataDestinationReadOnly = ({
  value
}: {
  value: IAutomationActionTemplateSyncToDataDestination;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const sourceOptions = useDadoFieldOptions(org.customUserFields);
  const labelByKey = useMemo(() => {
    const map: { [key: string]: string } = {};
    sourceOptions.forEach((o) => {
      map[o.key] = o.label;
    });
    return map;
  }, [sourceOptions]);

  const cfg = readSyncConfig(value);
  const def = cfg.destination
    ? getSyncDestinationDefinition(cfg.destination.type)
    : undefined;

  if (!cfg.destination || !cfg.mappings.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t('No fields configured to sync.')}
      </Typography>
    );
  }

  // For the Dado-field destination the target is itself a Dado field key, so
  // resolve it to a human label just like the source.
  const isUserProfile =
    cfg.destination.type === InternalStorageInstructionType.USER_PROFILE;

  return (
    <FlexContainerVertical spacing={1} fullWidth>
      <Typography variant="body2">
        {t('Destination')}: {def ? t(def.label) : cfg.destination.type}
      </Typography>
      {cfg.mappings.map((m, i) => (
        <FlexContainer key={i} spacing={1} alignItems="center">
          <span>{labelByKey[m.sourceField] || m.sourceField}</span>
          <ArrowRight size={14} />
          <span>{isUserProfile ? labelByKey[m.target] || m.target : m.target}</span>
        </FlexContainer>
      ))}
    </FlexContainerVertical>
  );
};
